import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import ImageUploader from './components/ImageUploader';
import ResultDisplay from './components/ResultDisplay';
import Turnstile from './components/Turnstile';
import axios from 'axios';

const FOXID_LOGO = 'https://foxid.com.au/wp-content/uploads/2025/06/FoxID-Logo-V3.2-scaled.png';

function App() {
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const appRef = useRef(null);

  // Embed mode (via URL param or iframe) hides the header/footer chrome.
  const urlParams = new URLSearchParams(window.location.search);
  const isEmbed = urlParams.get('embed') === 'true' || window.self !== window.top;

  // Load public runtime config (Turnstile site key).
  useEffect(() => {
    axios.get('/api/config')
      .then((r) => setTurnstileSiteKey(r.data?.turnstileSiteKey || null))
      .catch(() => setTurnstileSiteKey(null));
  }, []);

  // Auto-resize the iframe when embedded.
  useEffect(() => {
    if (isEmbed && appRef.current) {
      const sendHeight = () => {
        const height = appRef.current.scrollHeight;
        window.parent.postMessage({ type: 'FOXID_RESIZE', height }, '*');
      };
      sendHeight();
      const resizeObserver = new ResizeObserver(sendHeight);
      resizeObserver.observe(appRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [isEmbed, result, loading, error]);

  const handleFrontImageChange = (file) => {
    setFrontImage(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setFrontPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setFrontPreview(null);
    }
  };

  const handleBackImageChange = (file) => {
    setBackImage(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setBackPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setBackPreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!frontImage) {
      setError('Please upload the front of your ID document.');
      return;
    }

    if (turnstileSiteKey && !turnstileToken) {
      setError('Please complete the verification challenge.');
      return;
    }

    if (!backImage) {
      const proceed = window.confirm(
        'No back image provided.\n\n' +
        'For best results (back address, back ID image), please capture the back of your ID.\n\n' +
        'Click OK to continue without it, or Cancel to add it.'
      );
      if (!proceed) return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('ID_Front_Image', frontImage);
    if (backImage) formData.append('ID_Back_Image', backImage);
    if (turnstileToken) formData.append('turnstileToken', turnstileToken);

    try {
      const response = await axios.post('/api/foxid/scan', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });

      setResult(response.data);

      if (isEmbed && window.parent !== window) {
        window.parent.postMessage({ type: 'FOXID_RESULT', data: response.data }, '*');
      }
    } catch (err) {
      if (err.response?.data?.message) setError(err.response.data.message);
      else if (err.message) setError(err.message);
      else setError('An unexpected error occurred. Please try again.');
      // Reset the Turnstile token so the next attempt re-challenges.
      setTurnstileToken('');
      try { window.turnstile?.reset?.(); } catch { /* ignore */ }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFrontImage(null);
    setBackImage(null);
    setFrontPreview(null);
    setBackPreview(null);
    setResult(null);
    setError(null);
    setTurnstileToken('');
    try { window.turnstile?.reset?.(); } catch { /* ignore */ }
  };

  return (
    <div ref={appRef} className={`app ${isEmbed ? 'embed-mode' : ''}`}>
      {!isEmbed && (
        <header className="app-header">
          <div className="logo">
            <img src={FOXID_LOGO} alt="FoxID" />
          </div>
          <p className="subtitle">AI-Powered ID Document OCR Demo</p>
        </header>
      )}

      <main className="app-main">
        <div className="container">
          {!result ? (
            <form onSubmit={handleSubmit} className="upload-form">
              <div className="upload-section">
                <h2>Upload ID Documents</h2>
                <div className="upload-grid">
                  <ImageUploader
                    label="Front of ID"
                    required
                    preview={frontPreview}
                    onImageChange={handleFrontImageChange}
                    description="Driver's Licence, Passport, or ID Card"
                  />
                  <ImageUploader
                    label="Back of ID"
                    preview={backPreview}
                    onImageChange={handleBackImageChange}
                    description="Optional - Required for some documents"
                  />
                </div>
              </div>

              {error && (
                <div className="error-message">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="2"/>
                    <path d="M10 6V11M10 14V14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  {error}
                </div>
              )}

              {turnstileSiteKey && (
                <div className="turnstile-row">
                  <Turnstile siteKey={turnstileSiteKey} onToken={setTurnstileToken} />
                </div>
              )}

              <button
                type="submit"
                className="submit-button"
                disabled={loading || !frontImage || (!!turnstileSiteKey && !turnstileToken)}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Scan Document
                  </>
                )}
              </button>
            </form>
          ) : (
            <ResultDisplay result={result} onReset={handleReset} frontPreview={frontPreview} backPreview={backPreview} />
          )}
        </div>
      </main>

      {!isEmbed && (
        <footer className="app-footer">
          <p>FoxID OCR Demo &copy; {new Date().getFullYear()} | <a href="https://foxid.com.au" target="_blank" rel="noopener noreferrer">foxid.com.au</a> | Powered by IDV Pacific</p>
        </footer>
      )}
    </div>
  );
}

export default App;
